import {
  WhatsAppAttachmentSchema,
  WhatsAppMessageSchema,
  WhatsAppReactionSchema,
} from "@/app/shared/services/volateq-api/api-schemas/whatsapp-schema";
import { AnalysisNotesHelper } from "@/app/shared/services/helper/analysis-notes-helper";

export type WhatsAppAttachmentKind = "image" | "video" | "audio" | "file";

export interface WhatsAppMediaAttachmentRef {
  messageId: string;
  attachment: WhatsAppAttachmentSchema;
}

/** One emoji's reactions on a WhatsApp message, aggregated across reactors. */
export interface WhatsAppGroupedReaction {
  emoji: string;
  count: number;
  /** Whether the connected company WhatsApp account is among the reactors. */
  reactedByMe: boolean;
  /** Display names of the reactors, for the chip tooltip. */
  reactorNames: string[];
}

export class WhatsAppHelper {
  /** Escapes, linkifies and converts newlines of a WhatsApp message text to HTML. */
  static renderMessageHtml(text: string): string {
    return AnalysisNotesHelper.renderDescriptionHtml(text);
  }

  static formatDateTime(isoDate: string | null): string {
    return isoDate ? AnalysisNotesHelper.formatDateTime(isoDate) : "";
  }

  static attachmentKind(attachment: WhatsAppAttachmentSchema): WhatsAppAttachmentKind {
    const mimetype = attachment.mimetype || "";
    if (mimetype.startsWith("image/")) {
      return "image";
    }
    if (mimetype.startsWith("video/")) {
      return "video";
    }
    if (mimetype.startsWith("audio/")) {
      return "audio";
    }

    switch (attachment.type) {
      case "img":
        return "image";
      case "video":
        return "video";
      case "audio":
        return "audio";
      default:
        return "file";
    }
  }

  /** Whether the attachment is media that gets rendered inline (image / video / voice message). */
  static isMediaAttachment(attachment: WhatsAppAttachmentSchema): boolean {
    return WhatsAppHelper.attachmentKind(attachment) !== "file";
  }

  /** Collects all inline-renderable media attachments of the messages, with their message id. */
  static collectMediaAttachments(messages: WhatsAppMessageSchema[]): WhatsAppMediaAttachmentRef[] {
    return messages.flatMap(message =>
      message.attachments
        .filter(attachment => WhatsAppHelper.isMediaAttachment(attachment))
        .map(attachment => ({ messageId: message.id, attachment })),
    );
  }

  /** Unique sender attendee ids that can be used to load profile pictures. */
  static collectSenderAttendeeIds(messages: WhatsAppMessageSchema[]): string[] {
    const attendeeIds = new Set<string>();
    for (const message of messages) {
      if (message.is_event || !message.sender_attendee_id) {
        continue;
      }
      attendeeIds.add(message.sender_attendee_id);
    }
    return Array.from(attendeeIds);
  }

  /** Initials for a display name (e.g. "Johannes Hirth" → "JH", "You" → "Y"). */
  static senderInitials(displayName: string): string {
    const parts = displayName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return "?";
    }
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  static formatLinkableChatLabel(
    chatType: number | null,
    chatName: string | null,
    labels: { group: string; direct: string; unnamed: string },
  ): string {
    const typeLabel = chatType === 1 ? labels.group : labels.direct;
    const name = (chatName || "").trim() || labels.unnamed;
    return `${typeLabel} — ${name}`;
  }

  /**
   * Aggregates a message's reactions by emoji, preserving the order in which each emoji
   * first appeared. `ownSenderLabel` is used when a reaction has is_sender but no name.
   */
  static groupReactions(
    reactions: WhatsAppReactionSchema[],
    ownSenderLabel: string,
    unknownSenderLabel: string,
  ): WhatsAppGroupedReaction[] {
    const grouped = new Map<string, WhatsAppGroupedReaction>();

    for (const reaction of reactions) {
      let group = grouped.get(reaction.value);
      if (!group) {
        group = { emoji: reaction.value, count: 0, reactedByMe: false, reactorNames: [] };
        grouped.set(reaction.value, group);
      }

      group.count += 1;
      if (reaction.is_sender) {
        group.reactedByMe = true;
      }

      const reactorName = reaction.is_sender
        ? (reaction.sender_name || ownSenderLabel)
        : (reaction.sender_name || unknownSenderLabel);
      group.reactorNames.push(reactorName);
    }

    return Array.from(grouped.values());
  }
}
